<script lang="ts" setup>
import { createSidebar, NavigationSidebar, VIcon } from "@app/core";
import { computed } from "vue";
import { RouterView } from "vue-router";

import router from "@/app/router";
import { useMenu } from "@/app/router/composables/useMenu";
import { prefetchRoute } from "@/app/router/utils/prefetch";
import { PartnershipContextBlock } from "@/features/partnership";
import { ThemeToggle } from "@/features/theme";
import { PERSISTENT_FILTER_PARAMS } from "@/shared/config/globalFilters.config";

const { menuItems } = useMenu();

const sidebar = createSidebar({
  items: menuItems,
  brandName: "SO Insights",
  storageKey: "insights-sidebar",
  persistCollapse: true,
  persistentQueryParams: PERSISTENT_FILTER_PARAMS,
});

const contentMargin = computed(() => ({
  "lg:ml-64": !sidebar.isCollapsed.value,
  "lg:ml-[4.5rem]": sidebar.isCollapsed.value,
}));
</script>

<template>
  <div class="flex min-h-screen bg-mainBg text-mainText overflow-x-hidden p-4">
    <!-- ── Sidebar ────────────────────────────────────────────────────────── -->
    <NavigationSidebar
      :sidebar="sidebar"
      @prefetch="(to) => prefetchRoute(router, to)"
    >
      <template #subtitle-block>
        <PartnershipContextBlock :collapsed="sidebar.isCollapsed.value" />
      </template>

      <template #footer-end>
        <div>
          <!-- Mobile drawer only — on desktop this block lives in #subtitle-block.
               #footer-end is forwarded to BOTH the desktop footer and the mobile drawer,
               so this stays here with lg:hidden. It supersedes the old UserMenu (same user
               info + password change + logout) and adds partnership switching on mobile.
               placement="top-left" so the dropdown opens upward from the bottom footer. -->
          <PartnershipContextBlock
            class="lg:hidden"
            placement="top-left"
          />

          <ThemeToggle :compact="sidebar.isCollapsed.value" />
        </div>
      </template>
    </NavigationSidebar>

    <!-- ── Content Area ───────────────────────────────────────────────────── -->
    <div
      :class="contentMargin"
      class="flex-1 flex flex-col transition-all duration-300 overflow-x-hidden
             bg-secondaryBg rounded-xl shadow-lg min-h-[calc(100vh-2rem)]"
    >
      <!-- Mobile header — visible only on <lg screens -->
      <header class="bg-secondaryBg border-b border-base-300 flex-shrink-0 lg:hidden rounded-t-xl">
        <div class="flex items-center justify-between h-16 px-4">
          <button
            aria-label="Open menu"
            class="p-2 rounded-lg hover:bg-base-200 text-neutral transition-colors"
            type="button"
            @click="sidebar.toggleMobile()"
          >
            <VIcon
              :size="24"
              icon="mdi:menu"
            />
          </button>
          <span class="text-lg font-semibold text-primary">SO Insights</span>
          <div class="w-10" />
        </div>
      </header>

      <!-- Page frame — the workspace layout (or an error page) renders inside it -->
      <main class="flex-1 flex flex-col overflow-x-hidden">
        <div class="flex-1 py-4 px-4 sm:px-6 flex flex-col min-w-0">
          <RouterView />
        </div>
      </main>
    </div>
  </div>
</template>
